import React, { CSSProperties, useId } from 'react';

/**
 * HumanReplica® Animated Identity Logo — v1.4
 *
 * Exposed states (core six + listening):
 *   default    – idle breath, cyan corner, ambient glow (stdDev 3.5)
 *   thinking   – faster opacity pulse, cyan corner, focused glow (stdDev 4.5)
 *   listening  – subtle scale pulse (scale 1→1.08→1), cyan, focused glow
 *   approval   – amber corner, high-intensity glow (stdDev 5.5)
 *   escalated  – red corner, glow off, contract animation
 *   success    – green corner, expanding glow synced with bloom (stdDev 4.5→6)
 *   sovereign  – silver corner, no glow, completely still
 *
 * Reserved (tokens defined, not exposed):
 *   processing, warning, enterprise, creative
 *
 * Rules:
 *   • Frame brand color = #0F172A. frameDark adapts it for dark surfaces.
 *   • Corner strokeWidth scales by size: ≥48→10, ≥32→7, <32→5.
 *   • Animations only activate at size >= 40 (showMotion).
 *   • Glow is suppressed in light mode via CSS (not this component).
 *   • All animations are inside @media (prefers-reduced-motion: no-preference).
 *   • renderVoid only renders at size >= 64.
 *   • foreignObject falls back silently if unavailable (T1 note).
 */

export type HRLogoState =
  | 'default'
  | 'thinking'
  | 'listening'
  | 'approval'
  | 'escalated'
  | 'success'
  | 'sovereign';

const STATE_COLORS: Record<HRLogoState, string> = {
  default:   'var(--accent)',
  thinking:  'var(--accent)',
  listening: 'var(--accent)',
  approval:  '#FFB800',
  escalated: '#FF003C',
  success:   '#00FF66',
  sovereign: '#F1F5F9',
};

const GLOW_STATES: Record<HRLogoState, boolean> = {
  default:   true,
  thinking:  true,
  listening: true,
  approval:  true,
  escalated: false,
  success:   true,
  sovereign: false,
};

/**
 * Per-state glow stdDeviation — 2D: brand spec calibrated intensities.
 *   ambient  (3.5) — default idle: soft, barely-there glow
 *   focused  (4.5) — thinking / listening: purposeful, steady
 *   high     (5.5) — approval: draws the eye, demands human attention
 *   bloom    (6.0) — success: maximum expansion at peak of bloom keyframe
 */
const GLOW_STD_DEV: Record<HRLogoState, number> = {
  default:   3.5,
  thinking:  4.5,
  listening: 4.5,
  approval:  5.5,
  escalated: 0,    // glow off — not rendered
  success:   6.0,
  sovereign: 0,    // glow off — not rendered
};

export interface HRLogoProps {
  state?: HRLogoState;
  size?: number;
  showGlow?: boolean;
  className?: string;
  /**
   * Orbit trace animation around the full frame perimeter.
   *   true     — continuous animation (3s loop)
   *   'paused' — trace visible but frozen (useful for loading states)
   * Only animates at size >= 40.
   */
  orbit?: boolean | 'paused';
  /**
   * Render function for center void content.
   * Only rendered when size >= 64.
   * Permitted content: countdown timers, contextual data scenes.
   * Face/portrait = marketing/external only — never product UI.
   * Falls back silently if foreignObject is unavailable (mobile).
   */
  renderVoid?: () => React.ReactNode;
}

const HRLogo: React.FC<HRLogoProps> = ({
  state = 'default',
  size = 32,
  showGlow,
  className = '',
  orbit = false,
  renderVoid,
}) => {
  const uid = useId().replace(/:/g, '');
  const filterId = `hr-glow-${uid}`;
  const shadowId = `hr-shadow-${uid}`;
  const clipId = `hr-clip-${uid}`;

  // Animations only for size >= 40; below that, render static.
  const showMotion = size >= 40;

  // renderVoid only at size >= 64 (T3 safe containment, brand spec 2G)
  const showVoid = !!renderVoid && size >= 64;

  const color = STATE_COLORS[state];
  const glowActive = showGlow !== undefined ? showGlow : GLOW_STATES[state];
  const stdDev = GLOW_STD_DEV[state];

  // Corner stroke-width is fixed at 10 in the 100-unit viewBox (Graphic Chart v1.0).
  // The viewBox handles proportional scaling at any rendered size — no manual override needed.
  const cornerStroke = 10;

  const motionClass = showMotion ? `hr-state-${state}` : '';
  const orbitClass = showMotion && orbit ? (orbit === 'paused' ? 'hr-orbit-paused' : 'hr-orbit') : '';

  return (
    <svg
      id={`hr-logo-${uid}`}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="HumanReplica logo"
      role="img"
      className={['hr-logo-premium', motionClass, orbitClass, className].filter(Boolean).join(' ')}
      style={{ '--hr-state': color, flexShrink: 0 } as CSSProperties}
    >
      <defs>
        {/* Per-state glow filter — stdDeviation drives intensity (2D) */}
        {glowActive && (
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={stdDev} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}

        {/* Clip path for renderVoid — safe inset avoids squircle corner bleed (T3) */}
        {showVoid && (
          <clipPath id={clipId}>
            {/* Graphic Chart v1.0: safe inset = x=13 y=13 w=74 h=74 rx=13 */}
            <rect x="13" y="13" width="74" height="74" rx="13" ry="13" />
          </clipPath>
        )}
      </defs>

      {/* Frame — adapts to theme via CSS variable var(--hr-frame) */}
      <rect
        className="hr-frame"
        x="8" y="8"
        width="84" height="84"
        rx="18" ry="18"
        fill="none"
        stroke="var(--hr-frame)"
        strokeWidth="8"
      />

      {/* Glow layer — suppressed in light mode via CSS, off for no-glow states */}
      <path
        className="hr-glow-layer"
        d="M8 52 L8 74 Q8 92 26 92 L44 92"
        fill="none"
        stroke={color}
        strokeWidth={cornerStroke}
        strokeLinecap="round"
        filter={glowActive ? `url(#${filterId})` : undefined}
        opacity={glowActive ? 0.6 : 0}
      />

      {/* Active corner — bottom-left, driven by --hr-state */}
      <path
        className="hr-corner"
        d="M8 52 L8 74 Q8 92 26 92 L44 92"
        fill="none"
        stroke={color}
        strokeWidth={cornerStroke}
        strokeLinecap="round"
      />

      {/* Processing trace path — hidden by default, activated via JS */}
      <path
        className="hr-trace"
        d="M44 92 L82 92 Q92 92 92 82 L92 18 Q92 8 82 8 L18 8 Q8 8 8 18 L8 52"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="200"
        strokeDashoffset="200"
        opacity="0"
      />

      {/* Center void content — 2G: renderVoid prop, size >= 64 only */}
      {showVoid && (() => {
        try {
          return (
            <foreignObject
              x="13" y="13"
              width="74" height="74"
              clipPath={`url(#${clipId})`}
            >
              <div
                // @ts-expect-error — xmlns required for foreignObject in SVG
                xmlns="http://www.w3.org/1999/xhtml"
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {renderVoid()}
              </div>
            </foreignObject>
          );
        } catch {
          // T1: foreignObject unavailable — silently skip, frame + corner still render
          return null;
        }
      })()}
    </svg>
  );
};

export default HRLogo;
